<template>
  <div
    class="d-flex-hide flex-column justify-space-between h-full"
    v-if="show"
  >
    <div class="pa-4 h-full">
      <Details
        @edit="editMode = true"
        v-if="!editMode"
      ></Details>
      <Form
        v-show="editMode"
        ref="form"
        @loaded="handleLoaded"
      >
      </Form>
      <v-row
        class="mt-2"
        v-if="authorized"
      >
        <template v-if="edit_item.isDecorated">
          <v-col
            cols="12"
            class="mt-2"
          >
            <mcs-card
              outlined
              :title="edit_item.finalImageUrl ? 'Your Artwork Is Ready' : ''"
              :subtitle="
                edit_item.finalImageUrl
                  ? 'If you’d like to change it, upload a new image to replace the one shown.'
                  : ''
              "
              :loading="uploading"
              class="hover-pointer"
              @click="showFileSelect"
            >
              <v-img
                v-if="edit_item.finalImageUrl"
                :src="edit_item.finalImageUrl"
              ></v-img>
              <div
                v-else
                class="text-center"
              >
                <v-icon>mdi-plus</v-icon>
                <div>Upload Your Artwork</div>
                <div class="caption">Choose an image to customize your promotional product.</div>
              </div>

              <input
                ref="hiddenFileUpload"
                type="file"
                style="display: none"
                accept="image/*"
                @change="clickFileSelected"
              />

              <CropperDialog ref="cropper" />

              <template
                #actions
                v-if="edit_item.finalImageUrl"
              >
                <mcs-btn
                  block
                  color="black"
                  outlined
                >
                  Change Artwork
                </mcs-btn>
              </template>
            </mcs-card>
          </v-col>
          <v-col
            cols="12"
            class="mt-2"
          >
            <mcs-card
              outlined
              title="Request a Proof"
              subtitle="We’ll send it within 24 hours after you place your order."
              class="hover-pointer"
            >
              <v-checkbox
                v-model="edit_item.includeProof"
                label="Yes, send me a proof before fulfilling"
                hide-details
              />
            </mcs-card>
          </v-col>
        </template>
        <v-col cols="12">
          <mcs-card
            title="Shipping"
            @click.native="showShipping"
          >
            <template #title-append>
              <mcs-btn
                v-if="shipping_items.length"
                color="primary"
                text
              >
                Edit
              </mcs-btn>
            </template>
            <template v-if="shipping_items.length">
              <v-row
                v-for="(item, i) in shipping_items"
                :key="i"
                dense
              >
                <v-col cols="auto">
                  <v-icon
                    :color="item.iconColor"
                    x-small
                  >
                    {{ item.icon }}
                  </v-icon>
                </v-col>
                <v-col>
                  {{ item.label }}
                  <span class="text-decoration-underline">{{ item.text }}</span>
                </v-col>
              </v-row>
              <mcs-btn
                v-if="!edit_item.delivery"
                block
                outlined
                large
                color="black"
                class="mt-4"
              >
                Select Delivery
              </mcs-btn>
            </template>
            <mcs-btn
              v-else
              block
              outlined
              large
              color="black"
              prepend-icon="mdi-plus-circle"
            >
              Add Address
            </mcs-btn>
          </mcs-card>
        </v-col>
      </v-row>
    </div>

    <Portal
      v-if="!hideFooter"
      :to="`drawerAppend-${drawerId}`"
    >
      <v-divider />
      <v-row class="pa-2 ma-0">
        <v-col cols="auto">
          <mcs-btn
            v-if="!authorized"
            large
            color="black"
            :loading="saving"
            @click="login"
          >
            Continue
          </mcs-btn>
          <mcs-btn
            v-else
            large
            color="black"
            :loading="saving"
            :disabled="disableButton"
            @click="save($event)"
          >
            {{ buttonText }}
          </mcs-btn>
        </v-col>
        <v-col class="my-auto">
          <v-progress-circular
            v-if="loading_price"
            :size="20"
            color="primary"
            indeterminate
          />
          <template v-else>
            <div class="font-weight-bold d-flex align-center">
              <div
                v-if="totalPriceOriginal > totalPrice"
                class="font-weight-bold text-decoration-line-through mr-2"
              >
                {{ totalPriceOriginal | currency }}
              </div>
              <div :class="{ 'success--text': totalPriceOriginal > totalPrice }">
                {{ totalPrice | currency }}
              </div>
            </div>
            <div
              v-if="!authorized"
              class="body-2"
            >
              *Shipping not included
            </div>
            <div
              v-else-if="deliveryPrice"
              class="body-2"
            >
              Delivery {{ edit_item.delivery.earliestDate | regularDateNoYear }} -
              {{ edit_item.delivery.latestDate | regularDateNoYear }} (+{{
                edit_item.delivery.price | currency
              }})
            </div>
          </template>
        </v-col>
      </v-row>
    </Portal>
  </div>
</template>

<script>
import CropperDialog from '@/components/print/checkout/edititem/CropperDialog';
import Details from '@/components/print/checkout/edititem/promo/Details';
import Form from '@/components/print/checkout/edititem/promo/Form';
import { ANALYTICS_EVENTS, DRAWERS, ROUTES } from '@/utils/constants';
import promo from '@/utils/mixins/promo';
import { mapGetters } from 'vuex';

export default {
  components: {
    CropperDialog,
    Form,
    Details
  },

  props: {
    drawerId: {
      type: String,
      default: ''
    },
    hideFooter: Boolean,
    iframed: Boolean,
    show: { type: Boolean, default: false }
  },

  mixins: [promo],

  data: () => ({
    saving: false,
    valid: true,
    loading: false,
    loaded: false,
    uploading: false,
    editMode: false,
    store: 'printcart'
  }),

  computed: {
    shipping_items() {
      const list = [];
      if (this.edit_item.addressId) {
        list.push({
          label: 'Address',
          text: this.addresses
            .filter((x) => x.id === this.edit_item.addressId)
            .map((x) => `${x.firstName} ${x.lastName} - ${x.line1} ${x.city}, ${x.postalCode}`)[0],
          icon: 'mdi-check-circle',
          iconColor: 'success'
        });
      }
      if (this.edit_item.delivery) {
        if (!this.edit_item.delivery.earliestDate) {
          if (this.edit_item.addressId) {
            list.push({
              label: 'Delivery Date not set',
              icon: 'mdi-alert-circle',
              iconColor: 'error'
            });
          }
        } else {
          list.push({
            label: 'Expected Delivery Date',
            text: `${this.$options.filters.regularDateNoYear(
              this.edit_item.delivery.earliestDate
            )} - ${this.$options.filters.regularDateNoYear(this.edit_item.delivery.latestDate)}`,
            icon: this.edit_item.delivery.valid ? 'mdi-check-circle' : 'mdi-alert-circle',
            iconColor: this.edit_item.delivery.valid ? 'success' : 'accent'
          });
        }
      }
      return list;
    },

    disableButton() {
      if (this.loading || this.loading_price) {
        return true;
      }
      if (this.shipping_items.length !== 2 || !this.edit_item.delivery?.valid) {
        return true;
      }
      if (this.edit_item.isDecorated && !this.edit_item.finalImageUrl) {
        return true;
      }

      return false;
    },

    isAdd() {
      return !this.edit_item.id;
    },

    buttonText() {
      return this.isAdd ? 'Add to cart' : 'Continue';
    },

    deliveryPrice() {
      return this.edit_item.delivery && this.edit_item.addressId
        ? this.edit_item.delivery.price
        : 0;
    },

    totalPrice() {
      // return this.deliveryPrice + this.price;
      return this.price;
    },

    totalPriceOriginal() {
      // return this.deliveryPrice + this.priceOriginal;
      return this.priceOriginal;
    },

    edit_item: {
      get() {
        return this.$store.getters['printcart/edit_item'];
      },
      set(val) {
        this.$store.dispatch('printcart/setEditItem', val);
      }
    },

    loading_price: {
      get() {
        return this.$store.getters['printcart/loading_price'];
      },
      set(val) {
        this.$store.dispatch('printcart/setLoadingPrice', val);
      }
    },

    ...mapGetters({
      price: 'printcart/price',
      priceOriginal: 'printcart/priceOriginal',
      addresses: 'user/addresses',

      isAdmin: 'user/isAdmin',
      authorized: 'user/authorized'
    })
  },

  created() {},

  methods: {
    save(evt) {
      this.saving = true;
      this.$store
        .dispatch('printcart/savePromoCartLine', this.edit_item)
        .then(() => {
          this.$emit('save');
        })
        .catch((error) => {
          this.logError(error, 'An error occurred while saving.');
          this.$mixpanel.trackEvent(
            ANALYTICS_EVENTS.PROMO.NAME,
            ANALYTICS_EVENTS.PROMO.ACTIONS.CART_ERROR,
            { message: 'Error saving. ' + error.message }
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },

    async loadData(showShipping) {
      this.loading = true;
      this.editMode = false;
      this.$refs.form.getPromoProduct(true, showShipping, true);
    },

    showShipping() {
      this.$store.commit('drawers/SET_DATA', {
        drawerId: DRAWERS.PRINT_CHECKOUT_SHIPPING,
        data: {}
      });
      this.$store.commit('drawers/OPEN_DRAWER', DRAWERS.PRINT_CHECKOUT_SHIPPING);
    },

    login() {
      this.goToLogin(
        `&promoProduct=${this.edit_item.productId}&type=buy_product`,
        `${window.location.origin}${ROUTES.PROMO.PATH}?productId=${this.edit_item.productId}`
      );
    },
    showFileSelect() {
      this.$refs.hiddenFileUpload.click();
    },
    clickFileSelected(e) {
      this.uploading = true;
      this.$store
        .dispatch('printcart/uploadPromoOriginal', e.target.files[0])
        .then((data) => {
          this.$refs.cropper.show({
            width: this.decoration.width,
            height: this.decoration.height,
            url: data.url,
            publicId: data.publicId
          });
        })
        .catch((error) => {
          this.logError(error, 'An error occurred while uploading image.');
        })
        .finally(() => {
          this.uploading = false;
        });
    },
    handleLoaded() {
      this.$emit('loaded');
      this.loading = false;
    }
  }
};
</script>

<style scoped></style>
